/* oxlint-disable sorare/enforce-monetary-reward-disclaimer
 *   JONUM spending limits, not rewards — disclaimer doesn't apply. */
import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import {
  FormattedDate,
  FormattedMessage,
  FormattedNumber,
  defineMessages,
} from 'react-intl';
import styled from 'styled-components';

import { JonumVerificationStatus } from '#src/__generated__/globalTypes.ts';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconAddressCard } from '#src/atoms/icons/system/iconAddressCard.ts';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyL, BodyS, LabelM, LabelS } from '#src/atoms/typography/index.tsx';
import { DialogKey } from '#src/components/navigation/WithDialogs/index.tsx';
import { SettingsSection } from '#src/components/settings/SettingsSection/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useQuery } from '#src/hooks/graphql/useQuery.ts';
import { useOpenDialog } from '#src/hooks/navigation/useOpenDialog.ts';
import { useTimeLeft } from '#src/hooks/useTimeLeft.tsx';

import { useJonumVerificationGuard } from '../JonumVerificationDialog/useJonumVerificationGuard.ts';
import { EditLimitDialog } from './EditLimitDialog/index.tsx';
import { SelfExclusionDialog } from './SelfExclusionDialog/index.tsx';
import type {
  JonumLimitsQuery,
  JonumLimitsQueryVariables,
} from './__generated__/index.graphql.ts';

const JONUM_LIMITS_QUERY = gql`
  query JonumLimitsQuery {
    currentUser {
      slug
      jonumVerification {
        id
        status
        selfExcludedUntil
        playtime {
          limitMinutes
          periodResetsAt
          usedMinutes
          pendingLimitChange {
            limitMinutes
            effectiveAt
          }
        }
        spending {
          limit {
            eurCents
          }
          periodResetsAt
          used {
            eurCents
          }
          pendingLimitChange {
            limit {
              eurCents
            }
            effectiveAt
          }
        }
      }
    }
  }
` as TypedDocumentNode<JonumLimitsQuery, JonumLimitsQueryVariables>;

const messages = defineMessages({
  title: {
    defaultMessage: 'Auto-limits',
  },
  description: {
    defaultMessage: 'Manage your weekly play time and spending limits.',
  },
});

const MANAGEABLE_STATUSES = new Set<string>([
  JonumVerificationStatus.VERIFIED,
  JonumVerificationStatus.PROVISIONALLY_VERIFIED,
  JonumVerificationStatus.IN_REMEDIATION,
]);

const Row = styled(Vertical).attrs({ gap: 0.5 })`
  padding: var(--unit) 0;
  &:not(:last-child) {
    border-bottom: 1px solid var(--c-nd-100);
  }
`;

const VerificationCard = styled(Vertical).attrs({ gap: 1.5 })`
  width: 100%;
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  background: rgba(var(--c-rgb-yellow-400), 0.08);
  border: 1px solid rgba(var(--c-rgb-yellow-400), 0.3);
`;

const VerificationTitleIcon = styled.span`
  color: var(--c-yellow-400);
`;

const ChangeLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: var(--c-blue-400);
  cursor: pointer;
  font: inherit;
`;

type LimitType = 'playtime' | 'spending';

type SelfExclusionRowProps = {
  selfExcludedUntil: Date | null | undefined;
};

const SelfExclusionRow = ({ selfExcludedUntil }: SelfExclusionRowProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { isEnded, message: timeLeftMessage } = useTimeLeft(
    selfExcludedUntil ?? null
  );
  const isExcluded = !!selfExcludedUntil && !isEnded;

  return (
    <Row>
      <Vertical gap={0.5}>
        {isExcluded ? (
          <>
            <BodyL bold color="var(--c-red-400)">
              <FormattedMessage defaultMessage="Self-excluded" />
            </BodyL>
            <BodyS color="var(--c-yellow-500)">
              <FormattedMessage
                defaultMessage="{time} remaining"
                values={{ time: timeLeftMessage }}
              />
            </BodyS>
          </>
        ) : (
          <Button size="medium" color="red" onClick={() => setDialogOpen(true)}>
            <Vertical gap={0}>
              <FormattedMessage defaultMessage="Self-exclude" />
              <LabelS color="var(--c-nd-300)">
                <FormattedMessage defaultMessage="Between 24h and 12 months" />
              </LabelS>
            </Vertical>
          </Button>
        )}
      </Vertical>

      <SelfExclusionDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </Row>
  );
};

export const JonumLimitsSection = () => {
  const { currentUser } = useCurrentUserContext();
  const { shouldShow: needsVerification } = useJonumVerificationGuard();
  const openDialog = useOpenDialog();
  const [editType, setEditType] = useState<LimitType | null>(null);

  const verification = currentUser?.jonumVerification;
  const shouldShow =
    verification && MANAGEABLE_STATUSES.has(verification.status);

  const { data } = useQuery(JONUM_LIMITS_QUERY, { skip: !shouldShow });

  if (needsVerification) {
    return (
      <VerificationCard>
        <Horizontal gap={1}>
          <VerificationTitleIcon>
            <FontAwesomeIcon icon={iconAddressCard} />
          </VerificationTitleIcon>
          <LabelM bold color="var(--c-white)">
            <FormattedMessage defaultMessage="Verify your account" />
          </LabelM>
        </Horizontal>
        <BodyS color="var(--c-nd-600)">
          <FormattedMessage defaultMessage="Managers located in France must verify their account to participate in Sorare, in accordance with French regulations." />
        </BodyS>
        <BodyS color="var(--c-nd-600)">
          <FormattedMessage defaultMessage="Verification only takes a few minutes and helps ensure a safe and responsible gaming environment." />
        </BodyS>
        <Button
          size="medium"
          color="secondary"
          onClick={() => openDialog(DialogKey.jonumVerification, 'true')}
          startIcon={<FontAwesomeIcon icon={iconAddressCard} />}
          fullWidth
        >
          <FormattedMessage defaultMessage="Verify my account" />
        </Button>
      </VerificationCard>
    );
  }

  if (!shouldShow) return null;

  const limits = data?.currentUser?.jonumVerification;
  if (!limits) return null;

  const playtimeLimitMinutes = limits.playtime.limitMinutes;
  const playtimeHours =
    playtimeLimitMinutes != null ? playtimeLimitMinutes / 60 : null;
  const playtimeUsedHours = limits.playtime.usedMinutes / 60;
  const playtimePending = limits.playtime.pendingLimitChange;
  const spendingLimitCents = limits.spending.limit?.eurCents;
  const spendingUsedCents = limits.spending.used.eurCents;
  const spendingPending = limits.spending.pendingLimitChange;
  const resetDate =
    limits.playtime.periodResetsAt ?? limits.spending.periodResetsAt;

  const currentPlaytimeForEdit = playtimeHours ? String(playtimeHours) : '';
  const currentSpendingForEdit =
    spendingLimitCents != null ? String(spendingLimitCents / 100) : '';

  const { selfExcludedUntil } = limits;

  return (
    <>
      <SettingsSection {...messages}>
        <Vertical gap={0}>
          <Row>
            <Horizontal gap={1}>
              <LabelM bold color="var(--c-white)">
                <FormattedMessage defaultMessage="Play time" />
              </LabelM>
              {playtimeHours != null && (
                <LabelM color="var(--c-nd-600)">
                  <FormattedMessage
                    defaultMessage="{hours}h"
                    values={{ hours: playtimeHours }}
                  />
                </LabelM>
              )}
              <ChangeLink onClick={() => setEditType('playtime')}>
                <LabelS color="var(--c-blue-400)">
                  <FormattedMessage defaultMessage="Change" />
                </LabelS>
              </ChangeLink>
            </Horizontal>
            <BodyS color="var(--c-nd-600)">
              <FormattedMessage
                defaultMessage="Current play time: {hours}h"
                values={{
                  hours: (
                    <FormattedNumber
                      value={playtimeUsedHours}
                      maximumFractionDigits={1}
                    />
                  ),
                }}
              />
            </BodyS>
            {!!playtimePending && (
              <BodyS color="var(--c-rewards-orange)">
                <FormattedMessage
                  defaultMessage="Pending change to {hours}h — effective {date}"
                  values={{
                    hours: playtimePending.limitMinutes / 60,
                    date: (
                      <FormattedDate
                        value={playtimePending.effectiveAt}
                        day="numeric"
                        month="short"
                      />
                    ),
                  }}
                />
              </BodyS>
            )}
          </Row>

          <Row>
            <Horizontal gap={1}>
              <LabelM bold color="var(--c-white)">
                <FormattedMessage defaultMessage="Spending" />
              </LabelM>
              {spendingLimitCents != null && (
                <LabelM color="var(--c-nd-600)">
                  <FormattedNumber
                    value={spendingLimitCents / 100}
                    style="currency"
                    currency="EUR"
                    maximumFractionDigits={0}
                  />
                </LabelM>
              )}
              <ChangeLink onClick={() => setEditType('spending')}>
                <LabelS color="var(--c-blue-400)">
                  <FormattedMessage defaultMessage="Change" />
                </LabelS>
              </ChangeLink>
            </Horizontal>
            <BodyS color="var(--c-nd-600)">
              <FormattedMessage
                defaultMessage="Current spending: {amount}"
                values={{
                  amount: (
                    <FormattedNumber
                      value={(spendingUsedCents ?? 0) / 100}
                      style="currency"
                      currency="EUR"
                      maximumFractionDigits={0}
                    />
                  ),
                }}
              />
            </BodyS>
            {spendingPending?.limit.eurCents != null && (
              <BodyS color="var(--c-rewards-orange)">
                <FormattedMessage
                  defaultMessage="Pending change to {amount} — effective {date}"
                  values={{
                    amount: (
                      <FormattedNumber
                        value={spendingPending.limit.eurCents / 100}
                        style="currency"
                        currency="EUR"
                        maximumFractionDigits={0}
                      />
                    ),
                    date: (
                      <FormattedDate
                        value={spendingPending.effectiveAt}
                        day="numeric"
                        month="short"
                      />
                    ),
                  }}
                />
              </BodyS>
            )}
          </Row>

          {!!resetDate && (
            <Row>
              <Horizontal gap={1}>
                <LabelM bold color="var(--c-white)">
                  <FormattedMessage defaultMessage="Limits reset" />
                </LabelM>
                <LabelM color="var(--c-nd-600)">
                  <FormattedDate
                    value={resetDate}
                    day="numeric"
                    month="long"
                    year="numeric"
                  />
                </LabelM>
              </Horizontal>
            </Row>
          )}

          <SelfExclusionRow selfExcludedUntil={selfExcludedUntil} />
        </Vertical>
      </SettingsSection>

      {!!editType && (
        <EditLimitDialog
          type={editType}
          open
          onClose={() => setEditType(null)}
          currentLimit={
            editType === 'playtime'
              ? currentPlaytimeForEdit
              : currentSpendingForEdit
          }
        />
      )}
    </>
  );
};
